import { poHasInvoiceRecord } from "./purchaseOrderInvoice";
import type { MfgPurchaseOrder, MfgPurchaseOrderLine } from "./purchaseOrderTypes";

const COMMITTED_STATUSES = new Set(["issued", "confirmed", "partially_received", "received"]);

export type PurchasingSpendSummary = {
  /** Predicted $ on issued+ POs (line/PO total, not invoice). */
  expectedUsd: number;
  /** Invoice-confirmed $ (Y). */
  invoicedUsd: number;
  /** Quality / receipt chargebacks. */
  chargebackUsd: number;
  /** Invoice $ minus chargebacks. */
  netAcceptedUsd: number;
  openPoCount: number;
  needsInvoiceCount: number;
  needsDeliveryCount: number;
};

function lineChargeback(line: Pick<MfgPurchaseOrderLine, "chargeback_usd">): number {
  const n = line.chargeback_usd;
  return n != null && Number.isFinite(n) ? n : 0;
}

export function orderChargebackUsd(
  order: Pick<MfgPurchaseOrder, "chargeback_total_usd" | "lines">
): number {
  if (order.chargeback_total_usd != null && Number.isFinite(order.chargeback_total_usd)) {
    return Math.round(order.chargeback_total_usd * 100) / 100;
  }
  let sum = 0;
  for (const line of order.lines ?? []) sum += lineChargeback(line);
  return Math.round(sum * 100) / 100;
}

export function poNeedsDeliveryConfirm(
  order: Pick<MfgPurchaseOrder, "status" | "invoice_recorded_at" | "invoice_total_usd">
): boolean {
  if (order.status === "draft" || order.status === "cancelled" || order.status === "received") {
    return false;
  }
  return poHasInvoiceRecord(order);
}

export function summarizePurchasingSpend(
  orders: readonly MfgPurchaseOrder[]
): PurchasingSpendSummary {
  let expectedUsd = 0;
  let invoicedUsd = 0;
  let chargebackUsd = 0;
  let openPoCount = 0;
  let needsInvoiceCount = 0;
  let needsDeliveryCount = 0;

  for (const order of orders) {
    if (!COMMITTED_STATUSES.has(order.status)) continue;
    const predicted = order.total ?? order.subtotal ?? 0;
    if (Number.isFinite(predicted)) expectedUsd += predicted;
    if (poHasInvoiceRecord(order) && order.invoice_total_usd != null) {
      invoicedUsd += order.invoice_total_usd;
    } else if (order.status !== "received") {
      needsInvoiceCount += 1;
    }
    chargebackUsd += orderChargebackUsd(order);
    if (order.status !== "received") openPoCount += 1;
    if (poNeedsDeliveryConfirm(order) && order.status !== "received") {
      needsDeliveryCount += 1;
    }
  }

  expectedUsd = Math.round(expectedUsd * 100) / 100;
  invoicedUsd = Math.round(invoicedUsd * 100) / 100;
  chargebackUsd = Math.round(chargebackUsd * 100) / 100;
  return {
    expectedUsd,
    invoicedUsd,
    chargebackUsd,
    netAcceptedUsd: Math.round((invoicedUsd - chargebackUsd) * 100) / 100,
    openPoCount,
    needsInvoiceCount,
    needsDeliveryCount,
  };
}

/** Remaining ordered qty by material (issued/open, not yet received). */
export function onOrderQtyByMaterialId(
  orders: readonly MfgPurchaseOrder[]
): Map<string, { material_id: string; material_name: string; qty: number }> {
  const map = new Map<string, { material_id: string; material_name: string; qty: number }>();
  for (const order of orders) {
    if (order.status === "draft" || order.status === "cancelled" || order.status === "received") {
      continue;
    }
    for (const line of order.lines ?? []) {
      if (!line.material_id) continue;
      const remaining = Math.max(0, line.quantity - (line.received_qty ?? 0));
      if (remaining <= 0) continue;
      const prev = map.get(line.material_id);
      if (prev) {
        prev.qty = Math.round((prev.qty + remaining) * 1000) / 1000;
      } else {
        map.set(line.material_id, {
          material_id: line.material_id,
          material_name: line.material_name,
          qty: Math.round(remaining * 1000) / 1000,
        });
      }
    }
  }
  return map;
}
